<script setup lang="ts">
import type { ProductHeaders } from '~/models/ProductHeaders'
import type { BaseItem } from '~/models/Content/BaseItem'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { KsDropdown } from '@aschehoug/kloss'
import arrayUtils from '~/utils/arrayUtils'
import useProductStore from '~/stores/product'
import useFilterStore from '~/stores/filter'
import useSlugify from '~/composables/useSlugify'
import { useContentHelper } from '~/composables/useContentHelper'

const props = withDefaults(defineProps<{
  resources?: BaseItem[]
  autoSelect?: boolean
  index: number
  enableGradeFilter?: boolean
  fullWidth?: boolean
  showNumberedHeaders?: boolean
  navigate?: boolean
  resettable?: boolean
  headerKey?: keyof ProductHeaders
}>(), {
  enableGradeFilter: true,
  showNumberedHeaders: true,
  headerKey: 'learningPath'
})

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const filterStore = useFilterStore()
const { contentFilters, setSelectedHeader, removeSelectedHeader } = filterStore
const { gradeFilterFunc } = contentFilters
const { productHeaders, isLoading, selectedHeaders } = storeToRefs(filterStore)
const { getProductFromResource } = useProductStore()
const { uniqueBy } = arrayUtils()
const { slugify } = useSlugify()

const currentHeader = computed(() =>
  selectedHeaders.value[props.index])

const numberedHeaders = computed(() => {
  if (props.resources)
    return []
  return productHeaders.value[props.headerKey]
})

const extraHeaders = computed(() => {
  if (props.resources)
    return props.resources

  const extraKey = props.headerKey === 'learningPath'
    ? 'flexibleContent'
    : props.headerKey

  return productHeaders.value[extraKey]
})

const { isHeader } = useContentHelper()

const allHeaders = computed(() => [...numberedHeaders.value, ...extraHeaders.value]
  .filter((header: BaseItem) => !props.enableGradeFilter || gradeFilterFunc(header))
  .filter(isHeader)
  .filter(uniqueBy('locationId')))

const showFilter = ref(true)
const hasHeaders = computed(() => showFilter.value && allHeaders.value.length > 0)

const selectHeader = async (header: BaseItem) => {
  const product = getProductFromResource(header)
  const headerIndex = selectedHeaders.value
    .findIndex(({ locationId }) => locationId === header.locationId)

  if (props.navigate && product?.subjects.length) {
    await router.push({
      name: 'header',
      params: {
        subject: product.subjects[0].toLowerCase(),
        locationId: header.locationId,
        slug: slugify(header.title)
      },
      query: route.query,
    })
  }

  if (headerIndex > -1) {
    removeSelectedHeader(header)
    return
  }

  setSelectedHeader([header])
}

const threeNonBreakingSpaces = '   '
const getHeaderName = (header: BaseItem) => {
  if (props.showNumberedHeaders && productHeaders.value[props.headerKey].includes(header)) {
    const index = allHeaders.value.findIndex(({ locationId }) => locationId === header.locationId)
    return `${index + 1}${threeNonBreakingSpaces}${header.title}`
  }
  return header.title
}

onMounted(() => {
  if (props.autoSelect && allHeaders.value.length > 0) {
    showFilter.value = false
    selectHeader(allHeaders.value[0])
  }
})

watch(selectedHeaders, () => {
  showFilter.value = !(currentHeader.value && allHeaders.value.length === 0)
}, { immediate: true })
</script>

<template>
  <KsDropdown
    v-if="hasHeaders"
    :model-value="currentHeader"
    :options="allHeaders"
    :option-id="(header: BaseItem) => header.locationId"
    :option-label="getHeaderName"
    :selectable="(h) => !isLoading && h.locationId !== currentHeader?.locationId"
    :placeholder="t('filters.chooseTheme')"
    shape="rounded"
    autolabel
    flexible
    :class="fullWidth && 'w-full'"
    :resettable
    @select="(header: BaseItem) => selectHeader(header)"
    @reset="() => removeSelectedHeader(currentHeader)"
  />
</template>
